import { Actie, ACTIES_QUERY_KEY } from "@/entities/actie";
import { Bericht, BERICHTEN_QUERY_KEY } from "@/entities/bericht";
import { BETROKKEN_TREINEN_QUERY_KEY, BetrokkenTrein } from "@/entities/betrokken-treinen";
import { CONTACTPERSONEN_QUERY_KEY, Contactpersoon } from "@/entities/contactpersoon";
import { DOSSIER_DETAILS_QUERY_KEY, DossierDetails } from "@/entities/dossier-details";
import { dossierOverzichtQueryKey } from "@/entities/dossier-overzicht";
import { LOGBOEK_QUERY_KEY, LogboekItem } from "@/entities/logboek";
import { Opstartplan, OPSTARTPLAN_QUERY_KEY } from "@/entities/opstartplan";
import { Suggestie, SUGGESTIES_QUERY_KEY } from "@/entities/suggestie";
import { TVV_BESTELDE_ROUTES_QUERY_KEY, TvvBesteldeRoute } from "@/entities/tvv";
import { AangevraagdeVestiging, VESTIGINGEN_QUERY_KEY } from "@/entities/vestiging";
import { Vsm, VSM_QUERY_KEY } from "@/entities/vsm";
import {
    ActieEvent,
    BerichtEvent,
    BetrokkenTreinEvent,
    ContactpersoonEvent,
    DossierDetailsEvent,
    EventReason,
    LogboekEvent,
    OpstartplanEvent,
    SuggestieEvent,
    TvvEvent,
    VestigingEvent,
    VsmEvent,
} from "@/infrastructure/rsocket";
import { useRemoveCacheListItem, useUpdateCacheListItem, useUpsertCacheListItem } from "@/infrastructure/tanstack-query/hooks";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { DOSSIERS_QUERY_KEY } from "./constants.ts";

export async function invalidateOpenDossiersQueries(queryClient: QueryClient, id: string | null = null) {
    await queryClient.invalidateQueries({ queryKey: dossierOverzichtQueryKey("Open") });
    if (id) {
        // When invalidating a specific dossier, we often need to refetch all its related queries (logboek, vestigingen, acties, etc.), so we use exact: false to invalidate the entire query subtree.
        await queryClient.invalidateQueries({ queryKey: [DOSSIERS_QUERY_KEY, id], exact: false });
    }
}

type DossierArrayProperties = {
    [ACTIES_QUERY_KEY]: { type: Actie; event: ActieEvent };
    [BERICHTEN_QUERY_KEY]: { type: Bericht; event: BerichtEvent };
    [CONTACTPERSONEN_QUERY_KEY]: { type: Contactpersoon; event: ContactpersoonEvent };
    [BETROKKEN_TREINEN_QUERY_KEY]: { type: BetrokkenTrein; event: BetrokkenTreinEvent };
    [LOGBOEK_QUERY_KEY]: { type: LogboekItem; event: LogboekEvent };
    [SUGGESTIES_QUERY_KEY]: { type: Suggestie; event: SuggestieEvent };
    [VESTIGINGEN_QUERY_KEY]: { type: AangevraagdeVestiging; event: VestigingEvent };
    [TVV_BESTELDE_ROUTES_QUERY_KEY]: { type: TvvBesteldeRoute; event: TvvEvent };
};
type UpdateArrayItem<T extends DossierArrayProperties[keyof DossierArrayProperties]["type"]> = (item: T) => T;
type CreateArrayItem<T extends DossierArrayProperties[keyof DossierArrayProperties]["type"]> = () => T;
type SortFnArrayItem<T extends DossierArrayProperties[keyof DossierArrayProperties]["type"]> = (a: T, b: T) => number;

function useUpdateArrayDossierPropertyQueriesData<P extends keyof DossierArrayProperties>(property: P) {
    type ValueType = DossierArrayProperties[P]["type"];

    const upsertCacheListItem = useUpsertCacheListItem();
    const updateCacheListItem = useUpdateCacheListItem();
    const removeCacheListItem = useRemoveCacheListItem();

    const create = useCallback(
        (dossierId: number, createFn: CreateArrayItem<ValueType>, sort?: SortFnArrayItem<ValueType>) => {
            upsertCacheListItem({
                queryKey: [DOSSIERS_QUERY_KEY, dossierId.toString(), property],
                idField: "id",
                createFn,
                sortFn: sort ? (items) => items.sort(sort) : undefined,
            });
        },
        [upsertCacheListItem, property]
    );

    const update = useCallback(
        (dossierId: number, itemId: number, updateFn: UpdateArrayItem<ValueType>, sort?: SortFnArrayItem<ValueType>) => {
            updateCacheListItem({
                queryKey: [DOSSIERS_QUERY_KEY, dossierId.toString(), property],
                idField: "id",
                idToUpdate: itemId,
                updateFn,
                sortFn: sort ? (items) => items.sort(sort) : undefined,
            });
        },
        [updateCacheListItem, property]
    );

    const remove = useCallback(
        (dossierId: number, idToDelete: number, sort?: SortFnArrayItem<ValueType>) => {
            removeCacheListItem<ValueType, "id">({
                queryKey: [DOSSIERS_QUERY_KEY, dossierId.toString(), property],
                idField: "id",
                idToDelete,
                sortFn: sort ? (items) => items.sort(sort) : undefined,
            });
        },
        [removeCacheListItem, property]
    );

    /**
     * Helper to completely handle create, update and delete based on eventReason
     */
    const handleEvent = useCallback(
        (dossierId: number, itemId: number, eventReason: EventReason, item: ValueType, sort?: SortFnArrayItem<ValueType>) => {
            switch (eventReason) {
                case "Nieuw":
                    create(dossierId, () => item, sort);
                    break;
                case "Wijziging":
                    update(dossierId, itemId, () => item, sort);
                    break;
                case "Verwijderd":
                    remove(dossierId, itemId, sort);
                    break;
            }
        },
        [create, remove, update]
    );

    return { create, update, remove, handleEvent };
}

type DossierSingletonProperties = {
    [DOSSIER_DETAILS_QUERY_KEY]: { type: DossierDetails | null; event: DossierDetailsEvent };
    [OPSTARTPLAN_QUERY_KEY]: { type: Opstartplan | null; event: OpstartplanEvent };
    [VSM_QUERY_KEY]: { type: Vsm | null; event: VsmEvent };
};

const singletonProperties = [DOSSIER_DETAILS_QUERY_KEY, OPSTARTPLAN_QUERY_KEY, VSM_QUERY_KEY] as const;

function isSingletonProperty(
    property: keyof DossierArrayProperties | keyof DossierSingletonProperties
): property is keyof DossierSingletonProperties {
    return singletonProperties.includes(property as keyof DossierSingletonProperties);
}

type UpdateSingleton<T extends DossierSingletonProperties[keyof DossierSingletonProperties]["type"]> = (item: T) => T;
type CreateSingleton<T extends DossierSingletonProperties[keyof DossierSingletonProperties]["type"]> = () => T;

function useUpdateSingletonDossierPropertyQueriesData<P extends keyof DossierSingletonProperties>(property: P) {
    type SingletonType = DossierSingletonProperties[P]["type"];
    const queryClient = useQueryClient();

    const update = useCallback(
        function updateDossierProperty(dossierId: number, itemId: number, updateFn: UpdateSingleton<SingletonType>) {
            queryClient.setQueryData<SingletonType>(
                [DOSSIERS_QUERY_KEY, dossierId.toString(), property],
                (item: SingletonType | undefined): SingletonType => {
                    if (!item) return null;
                    if (item.id === itemId) return updateFn(item);
                    return item;
                }
            );
        },
        [queryClient, property]
    );

    const create = useCallback(
        function createDossierProperty(dossierId: number, createFn: CreateSingleton<SingletonType>) {
            queryClient.setQueryData<SingletonType>([DOSSIERS_QUERY_KEY, dossierId.toString(), property], createFn);
        },
        [queryClient, property]
    );

    const remove = useCallback(
        function removeDossierProperty(dossierId: number, idToDelete: number) {
            queryClient.setQueryData<SingletonType>(
                [DOSSIERS_QUERY_KEY, dossierId.toString(), property],
                (item: SingletonType | undefined): SingletonType | undefined => {
                    if (item?.id === idToDelete) return null;
                    return item;
                }
            );
        },
        [queryClient, property]
    );

    /**
     * Helper to completely handle create, update and delete based on eventReason
     */
    const handleEvent = useCallback(
        (dossierId: number, itemId: number, eventReason: EventReason, item: SingletonType) => {
            switch (eventReason) {
                case "Nieuw":
                    create(dossierId, () => item);
                    break;
                case "Wijziging":
                    update(dossierId, itemId, () => item);
                    break;
                case "Verwijderd":
                    remove(dossierId, itemId);
                    break;
            }
        },
        [create, remove, update]
    );

    return { create, update, remove, handleEvent };
}

type ArrayResult<ValueType extends DossierArrayProperties[keyof DossierArrayProperties]["type"]> = {
    create: (dossierId: number, createFn: CreateArrayItem<ValueType>, sort?: SortFnArrayItem<ValueType>) => void;
    update: (dossierId: number, itemId: number, updateFn: UpdateArrayItem<ValueType>, sort?: SortFnArrayItem<ValueType>) => void;
    remove: (dossierId: number, idToDelete: number, sort?: SortFnArrayItem<ValueType>) => void;
    handleEvent: (dossierId: number, itemId: number, eventReason: EventReason, item: ValueType, sort?: SortFnArrayItem<ValueType>) => void;
};

type SingletonResult<ValueType extends DossierSingletonProperties[keyof DossierSingletonProperties]["type"]> = {
    create: (dossierId: number, createFn: CreateSingleton<ValueType>) => void;
    update: (dossierId: number, itemId: number, updateFn: UpdateSingleton<ValueType>) => void;
    remove: (dossierId: number, idToDelete: number) => void;
    handleEvent: (dossierId: number, itemId: number, eventReason: EventReason, item: ValueType) => void;
};

type Result<P extends keyof DossierSingletonProperties | keyof DossierArrayProperties> = P extends keyof DossierSingletonProperties
    ? SingletonResult<DossierSingletonProperties[P]["type"]>
    : P extends keyof DossierArrayProperties
      ? ArrayResult<DossierArrayProperties[P]["type"]>
      : never;

/**
 * Given a property P, construct typesafe functions to create, update and remove an item from the queryClient cache
 *
 * Example for array types:
 * ```
 * const { create, update, remove, handleEvent } = useUpdateDossierPropertyQueriesData("acties");
 * // Create new actie in queryData ["dossiers", dossierId, "acties" ]
 * create(() => Actie.fromDto(actieDto))
 * // Update actie in queryData ["dossiers", dossierId, "acties" ]
 * update((existingActie: Actie) => Actie.doUpdate(existingActie))
 * // Remove actie in queryData ["dossiers", dossierId, "acties" ]
 * remove(actieIdToDelete)
 * // Create, Update or Remove actie in queryData ["dossiers", dossierId, "acties" ] depending on `message.reason`
 * handleEvent(dossierId, actieId, message.reason, Actie.fromDto(actieDto))
 * ```
 *
 * Example for singleton:
 * ```
 * const { create, update, remove, handleEvent } = useUpdateDossierPropertyQueriesData("vsm");
 * // Create new actie in queryData ["dossiers", dossierId, "vsm" ]
 * create(() => Vsm.fromDto(vsmDto))
 * // Update vsm in queryData ["dossiers", dossierId, "vsm" ]
 * update((existingVsm: Vsm) => Vsm.doUpdate(existingVsm))
 * // Remove vsm in queryData ["dossiers", dossierId, "vsm" ]
 * remove(vsmIdToDelete)
 * // Create, Update or Remove actie in queryData ["dossiers", dossierId, "vsm" ] depending on `message.reason`
 * handleEvent(dossierId, vsmId, message.reason, Vsm.fromDto(vsmDto))
 * ```
 * @param property
 */
export function useUpdateDossierPropertyQueriesData<P extends keyof DossierSingletonProperties | keyof DossierArrayProperties>(
    property: P
): Result<P> {
    const singletonHandlers = useUpdateSingletonDossierPropertyQueriesData(property as keyof DossierSingletonProperties);
    const arrayHandlers = useUpdateArrayDossierPropertyQueriesData(property as keyof DossierArrayProperties);

    if (isSingletonProperty(property)) {
        return singletonHandlers as Result<P>;
    }
    return arrayHandlers as Result<P>;
}
